import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex';
import FlexItem from '@wix/da-ds/pkg/Flex/Item';
import ExclusiveButton from '@wix/da-ds/pkg/Buttons/ExclusiveButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import GiftAnimated from '@wix/da-ds/pkg/Icons/GiftAnimated';
import { BiData, GiveAdoptableGiftClickBiEvent } from '@wix/da-bi/pkg/events';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { ReferralType } from '@wix/da-bi/pkg/constants';

import { useRestrictToLoggedIn } from '../../../../../../utils/hooks/useRestrictToLoggedIn';
import useAdoptableState from '../../../AdoptableInfo/useAdoptableState';
import { useGiftNowAction } from '../../hooks';
import GiftExclusivePopup from './_partials/GiftExclusivePopup';
import s from './GiftButton.scss';

export interface Props {
  deviation: PapiDeviation;
  adoptableState: Partial<ReturnType<typeof useAdoptableState>>;
}

const GiftButton: React.FC<Props> = ({
  deviation,
  adoptableState: { fixedPrice = 0 },
}) => {
  const { t } = useTranslation();
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const [isGiftExcusivePopupShown, setIsGiftExclusivePopupShown] =
    useState(false);
  const showGiftPopup = useCallback(
    () => setIsGiftExclusivePopupShown(true),
    []
  );
  const [forUserId, setForUserId] = useState<number | undefined>(undefined);
  const [onGiftNow, biData] = useGiftNowAction(
    deviation,
    {
      purchase_price: fixedPrice,
    },
    forUserId
  );

  useEffect(() => {
    if (forUserId) {
      setForUserId(undefined);
      setIsGiftExclusivePopupShown(false);
      onGiftNow();
    }
  }, [forUserId, onGiftNow]);

  return (
    <BiLoggerContextProvider value={{ sectionname: 'side-rail-gift' }}>
      <ExclusiveButton
        key="gift-now"
        priority="secondary"
        variant="compact"
        onClick={restrictToLoggedIn(
          ReferralType.SHOP_ADOPTABLES,
          showGiftPopup
        )}
        biData={BiData<GiveAdoptableGiftClickBiEvent>(biData)}
      >
        <Flex alignItems="center" className={s['root']}>
          <FlexItem>
            <Flex inline alignItems="center">
              <GiftAnimated size={IconSize.MEDIUM} className={s['gift-icon']} />
            </Flex>
          </FlexItem>
          <FlexItem>
            {t('sidebar.exclusives.buttons.buyer.gift.label')}
          </FlexItem>
        </Flex>
      </ExclusiveButton>
      {isGiftExcusivePopupShown && (
        <GiftExclusivePopup
          requestClose={() => setIsGiftExclusivePopupShown(false)}
          deviation={deviation}
          onItemClick={user => setForUserId(user.userId)}
        />
      )}
    </BiLoggerContextProvider>
  );
};
GiftButton.displayName = 'GiftButton';

export default GiftButton;
